/* The guideline checks, client-side.
 *
 * WHY THIS EXISTS WHEN THE SERVER ALREADY HAS IT
 * The server's copy is the one that DECIDES — it gates submit, and a rule
 * enforced only here is a suggestion. This copy exists for latency: 6.1.1 puts
 * the AI check on the inspector, live, and a round trip per keystroke is not
 * feedback.
 *
 * They are kept in step by reading the same rules from the same guideline set
 * (8.4.2) rather than by hardcoding anything. When they do disagree, the server
 * wins — checkJob() below is advisory and submit() is authoritative. */

import type { Guideline, Subtask, Task } from "../api/types";
import { HAND_LABEL, isResolved } from "./format";

const TOL = 0.05; // 6.3.1 states this tolerance

/** 6.1.1's accepted mentions, verbatim. The check does NOT verify that the
 *  phrase matches the Hands field — any accepted mention passes. That is
 *  deliberate: a checker that demanded agreement would be measuring the
 *  annotator's phrasing rather than the footage. */
// THE SAME LIST AS app/api/checks.py DEFAULT_HAND_MENTIONS — test-enforced
// (5 Oct). The server's copy decides which sub-tasks the judge may lock; when
// this one was stricter ("right thumb" refused here, accepted there) a locked
// sub-task made its video impossible to submit.
export const HAND_MENTIONS = [
  "left hand", "right hand", "both hands", "one hand", "two hands",
  "hands", "fingertips", "left", "right",
];

const words = (s: string | null | undefined) =>
  (s ?? "").trim().split(/\s+/).filter(Boolean);

/** Whole-word matches. `escape` runs on the WORD, never on a slice of an
 *  already-escaped string — truncating one can cut a backslash escape in half. */
const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function blacklisted(description: string, list: string[]): string[] {
  const text = (description ?? "").toLowerCase();
  return list.filter((w) => new RegExp(`\\b${escape(w.toLowerCase())}\\b`).test(text));
}

const mentionsHand = (description: string, accepted: string[]) => {
  const text = (description ?? "").toLowerCase();
  return accepted.some((p) => text.includes(p.toLowerCase()));
};

/** NOTHING IS BEING DONE HERE — an idle span, not an action.
 *
 *  READ FROM THE VERB, NEVER FROM THE PROSE. Matching the description against
 *  "no action" would be matching the pipeline's current phrasing, which is a
 *  prompt away from changing, and would also exempt a real action that happened
 *  to contain the words.
 *
 *  `verb` IS NULL, NOT "idle", on the rows the pipeline actually writes. That
 *  distinction cost a wrong measurement: SQL's `verb <> 'idle'` is NULL — and
 *  therefore not true — for a null verb, so a query counted 7 of these where
 *  the running code found 67. JavaScript's `null !== "idle"` is true, so every
 *  one of them failed the object check in the browser while the database said
 *  they were fine. */
export const isIdle = (sub: Pick<Subtask, "verb">): boolean => {
  const v = (sub.verb ?? "").trim().toLowerCase();
  return v === "" || v === "idle";
};

export interface Rules {
  minWords: number;
  mustNameHands: boolean;
  blacklist: string[];
  handMentions: string[];
  minSubtaskSeconds: number;
  manualChecks: string[];
}

export function rulesFrom(g: Guideline | undefined): Rules {
  const r = g?.description_rules ?? {};
  return {
    minWords: r.min_words ?? 5,
    mustNameHands: r.must_name_hands ?? true,
    blacklist: r.blacklist ?? [],
    // READ FROM THE GUIDELINE, with our list as the fallback. This was
    // `HAND_MENTIONS` unconditionally, so `accepted_hand_mentions` — which
    // `api/checks.py` does read — was honoured by the half that decides and
    // ignored by the half the annotator sees. No guideline sets it today, which
    // is the only reason the two agreed.
    handMentions: r.accepted_hand_mentions ?? HAND_MENTIONS,
    // 8.1.3 says 0.5. THE SERVER'S DEFAULT IS 0.3, and the server is the half
    // that decides — this was hardcoded to 0.5, so 19 sub-tasks on the current
    // corpus were refused by Approve (checkSubtask blocks it, 6.1.2) over a
    // threshold `api/checks.py` would have passed. Same source or no source.
    minSubtaskSeconds: r.min_subtask_seconds ?? 0.3,
    manualChecks: r.manual_checks ?? [
      "No faces or personal information are readable in the retained footage.",
      "Object names follow the customer glossary.",
    ],
  };
}

/* ------------------------------------------------------- the AI check (6.1) */

/** Issues for ONE sub-task, in plain language. Empty means all clear.
 *  It never invents detail — every line names the rule that failed.
 *
 *  The FULL list, advisory notes included. Not everything here is worth
 *  BLOCKING Approve on — see `blockingIssues`, the narrower list 6.1.2
 *  actually refuses on, and the only caller on screen since "Check with AI"
 *  (which used to display this whole list) was removed on 24 Sep. */
export function checkSubtask(sub: Subtask, rules: Rules): string[] {
  const issues: string[] = [];
  const desc = sub.description ?? "";
  const n = words(desc).length;
  const idle = isIdle(sub);

  // NOT HELD TO THE ACTION RULES, because there is no action. The pipeline
  // writes "no action" with a null verb for a genuinely idle span — 53 of them
  // in the current corpus — and both of the rules below then fired on its own
  // output: "say what the person does in at least 5 words" on a sub-task where
  // the person does nothing, and "pick the object this action is performed on"
  // where there is no action to perform. Since checkSubtask BLOCKS Approve
  // (6.1.2), those sub-tasks could not be approved at all, and the only way
  // past was to invent a five-word sentence about an idle camera.
  //
  // The rules are not loosened — a REAL action still has to be described in
  // full. The 47 genuinely-short real actions in the corpus ("wipe bolt with
  // both", missing the hand word) keep failing, which is correct.
  if (!idle && n < rules.minWords)
    issues.push(
      `Description is too short — say what the person does in at least ${rules.minWords} words (this one has ${n}).`,
    );

  for (const w of blacklisted(desc, rules.blacklist))
    issues.push(`Blacklisted word "${w}" — name the exact object or action instead.`);

  if (rules.mustNameHands && sub.hands !== "idle" && !mentionsHand(desc, rules.handMentions))
    issues.push(
      `The description should mention the hand(s) — the Hands field says "${HAND_LABEL[sub.hands]}".`,
    );

  if (!idle && !sub.object_name)
    issues.push("Pick the object this action is performed on.");

  if (idle && sub.hands !== "idle")
    issues.push('An idle sub-task should have Hands set to "Idle".');

  const brief = spanWarning(sub, rules);
  if (brief) issues.push(brief);

  return issues;
}

/** The under-the-minimum-span note, split out from checkSubtask so it can be
 *  shown without being treated as a reason to refuse Approve — see
 *  `blockingIssues` below. */
function spanWarning(sub: Subtask, rules: Rules): string | null {
  const len = sub.end_seconds - sub.start_seconds;
  if (len < rules.minSubtaskSeconds)
    return `Sub-task is ${len.toFixed(2)}s — under the ${rules.minSubtaskSeconds}s minimum. Merge it into a neighbour if it isn't a real action.`;
  return null;
}

/** What actually refuses Approve (6.1.2) — a NARROWER list than checkSubtask.
 *
 *  THE SPAN WARNING IS ADVISORY, NOT A BLOCK. `api/checks.py` runs the mirror
 *  image of this same rule at submit and files it as severity "warning" —
 *  `TOO_BRIEF`, explicitly non-blocking, right alongside the comment "Errors
 *  block submit. Warnings do not." checkSubtask folded it in as a hard
 *  refusal anyway, so a genuinely short real action (a tap, a single flick of
 *  a switch) could never be marked reviewed from the workspace at all — the
 *  server would have accepted the same sub-task at submit without a second
 *  thought. checkSubtask still reports it — it is a true observation about
 *  the sub-task — it just must not be the reason Approve refuses. */
export function blockingIssues(sub: Subtask, rules: Rules): string[] {
  const brief = spanWarning(sub, rules);
  return checkSubtask(sub, rules).filter((issue) => issue !== brief);
}

/* ------------------------------------------------ the review checklist (6.3) */

export interface AutoCheck {
  id: string;
  blocking: boolean;
  pass: boolean;
  label: string;
  /** Optional: the review checklist computes one live ("3 sub-task(s) still
   *  pending"), the customer-guidelines view shows titles only — which is how
   *  the prototype presents them. */
  detail?: string;
  count: number;
}

/** Six checks. Five block; the sixth — "no rejected sub-tasks remain" — is
 *  advisory and NEVER blocks, so a deliberate reject is a choice the annotator
 *  can stand behind rather than an obstacle they route around. */
export function autoChecks(tasks: Task[], rules: Rules): AutoCheck[] {
  const subs = tasks.flatMap((t) => t.subtasks);
  const approved = subs.filter((s) => s.status === "APPROVED");
  const unresolved = subs.filter((s) => !isResolved(s));

  const tiling: string[] = [];
  for (const t of tasks) {
    const ss = t.subtasks;
    if (!ss.length) {
      tiling.push(`${t.title} has no sub-tasks`);
      continue;
    }
    if (Math.abs(ss[0].start_seconds - t.start_seconds) > TOL)
      tiling.push(`${t.title}: first sub-task does not start with the task`);
    if (Math.abs(ss[ss.length - 1].end_seconds - t.end_seconds) > TOL)
      tiling.push(`${t.title}: last sub-task does not end with the task`);
    for (let i = 0; i < ss.length - 1; i++)
      if (Math.abs(ss[i].end_seconds - ss[i + 1].start_seconds) > TOL)
        tiling.push(`${t.title}: gap or overlap at sub-task ${i + 2}`);
  }

  const noHands = approved.filter(
    (s) => rules.mustNameHands && s.hands !== "idle" &&
      !mentionsHand(s.description, rules.handMentions),
  );
  const incoherentIdle = approved.filter((s) => isIdle(s) && s.hands !== "idle");
  const handIssues = [...noHands, ...incoherentIdle];
  const vague = approved.filter((s) => blacklisted(s.description, rules.blacklist).length);
  // Idle spans are exempt, for the reason checkSubtask explains. Without this
  // the checklist reported a blocking failure on sub-tasks the annotator had
  // just been allowed to approve — the gate and the list disagreeing about the
  // same rule is worse than either being wrong on its own.
  const short = approved.filter(
    (s) => !isIdle(s) && words(s.description).length < rules.minWords);
  const rejected = subs.filter((s) => s.status === "REJECTED");

  return [
    {
      id: "resolved", blocking: true, pass: unresolved.length === 0,
      label: "Every sub-task has a verdict",
      detail: unresolved.length === 0
        ? `All ${subs.length} sub-tasks are approved, or rejected with a reason.`
        : `${unresolved.length} of ${subs.length} still need a verdict (a reject also needs its reason).`,
      count: unresolved.length,
    },
    {
      id: "tiling", blocking: true, pass: tiling.length === 0,
      label: "No gaps or overlaps",
      detail: tiling.length === 0
        ? "Sub-tasks cover every task end to end."
        : tiling.slice(0, 3).join("; ") + (tiling.length > 3 ? ` (+${tiling.length - 3} more)` : ""),
      count: tiling.length,
    },
    {
      id: "hands", blocking: true, pass: handIssues.length === 0,
      label: "Approved descriptions name the hand(s)",
      detail: handIssues.length === 0
        ? "Every approved description says which hand acts, or is coherently idle."
        : `${handIssues.length} approved sub-task${handIssues.length > 1 ? "s do" : " does"} not name the acting hand.`,
      count: handIssues.length,
    },
    {
      id: "blacklist", blocking: true, pass: vague.length === 0,
      label: "No vague words in approved descriptions",
      detail: vague.length === 0
        ? rules.blacklist.length
          ? `None of ${rules.blacklist.map((w) => `"${w}"`).join(", ")} appear.`
          : "No blacklist is configured for this job."
        : `${vague.length} approved description${vague.length > 1 ? "s use" : " uses"} a word from the blacklist.`,
      count: vague.length,
    },
    {
      id: "length", blocking: true, pass: short.length === 0,
      label: `Approved descriptions are ${rules.minWords} words or more`,
      detail: short.length === 0
        ? "Every approved description is long enough to be useful."
        : `${short.length} approved description${short.length > 1 ? "s are" : " is"} shorter than ${rules.minWords} words.`,
      count: short.length,
    },
    {
      id: "rejects", blocking: false, pass: rejected.length === 0,
      label: "No rejected sub-tasks remain",
      detail: rejected.length === 0
        ? "Nothing was rejected."
        : `${rejected.length} rejected sub-task${rejected.length > 1 ? "s" : ""} will go to QA with their reasons. This does not block submit.`,
      count: rejected.length,
    },
  ];
}

/** 6.4.1: five blocking checks plus both manual confirmations.
 *  "n of 7 complete" — the advisory one is not counted (6.3.4). */
export function submitGate(tasks: Task[], rules: Rules, manual: boolean[]) {
  const checks = autoChecks(tasks, rules);
  const blocking = checks.filter((c) => c.blocking);
  const manualDone = manual.filter(Boolean).length;
  return {
    checks,
    blocking,
    // COUNTED OVER ALL EIGHT, not the five blocking ones. The drawer lists six
    // automatic checks and two manual, so a header reading "4/7" described a
    // list nobody could find — the sixth automatic check was passing and
    // uncounted. Testing read 5 of 6 automatic passing and expected 5/8.
    //
    // A WARNING COUNTS AS DONE. The advisory check — "no rejected sub-tasks
    // remain" — renders as "!" rather than "✓" when the annotator has rejected
    // something on purpose, and it never blocks submit. Leaving it out of the
    // count made the counter stick one short of a total it could reach only by
    // un-rejecting work the annotator meant to reject: 4/8 on a video that was
    // ready to go. Testing hit this on mfg_assembling_001 and expected 5/8.
    //
    // `ok` still gates on BLOCKING only, so counting it changes what the number
    // says and not what submit permits.
    done: checks.filter((c) => c.pass || !c.blocking).length + manualDone,
    total: checks.length + manual.length,
    ok: blocking.every((c) => c.pass) && manualDone === manual.length,
  };
}
